import { useMemo } from 'react';
import {
  decimal,
  FOREIGN_LIMIT,
  isPlannedExit,
  LIST_A_LIMIT,
  money,
  ruleLevels,
  YOUNG_FOREIGN_ALLOWANCE,
} from '../lib/rules.ts';
import { useSquad } from '../lib/squadContext.ts';
import { C } from '../lib/theme.ts';
import type { RuleLevel } from '../types.ts';

/** Pill, number and bar colours that go with one severity level. */
interface Badge {
  label: string;
  bg: string;
  fg: string;
  border: string;
  valueFg: string;
  barBg: string;
}

interface RuleCard extends Badge {
  level: RuleLevel;
  title: string;
  /** Either a count or a formatted money figure. */
  value: string | number;
  limit: string;
  /** Bar fill width, already suffixed with '%'. */
  ratio: string;
  detail: string;
  hasAction: boolean;
  action: string;
}

function badge(level: RuleLevel): Badge {
  if (level === 'ok') {
    return { label: 'UYGUN', bg: C.ink, fg: C.bg, border: C.ink, valueFg: C.ink, barBg: C.ink };
  }
  if (level === 'warn') {
    return { label: 'RİSKLİ', bg: C.bg, fg: C.deep, border: C.red, valueFg: C.deep, barBg: C.red };
  }
  return { label: 'İHLAL', bg: C.red, fg: C.bg, border: C.red, valueFg: C.red, barBg: C.red };
}

/** The four rule cards and the wage budget slider, plus the collapsed rail. */
export default function RulesPanel() {
  const { state, dispatch, owned, listRules, squadRules } = useSquad();
  const wageCap = state.wageCap;
  const open = state.rulesPanelOpen;

  const cards = useMemo<RuleCard[]>(() => {
    // Wage still on the books after the planned loans and sales go through.
    const plannedWage = owned.filter(isPlannedExit).reduce((sum, p) => sum + p.wage, 0);

    const {
      foreign: foreignLevel,
      youngForeign: youngLevel,
      listA: listLevel,
      wage: wageLevel,
    } = ruleLevels(listRules, squadRules, wageCap);

    return [
      {
        level: foreignLevel,
        title: 'TFF · YABANCI SAYISI',
        value: listRules.foreign.length,
        limit: '/ ' + FOREIGN_LIMIT + ' üst sınır',
        ratio: Math.min(100, (listRules.foreign.length / FOREIGN_LIMIT) * 100) + '%',
        detail:
          'A Takım Listesi\'nde Türkiye A Millî Takımı\'nda oynama uygunluğu bulunmayan en fazla 14 futbolcu bulunabilir.',
        hasAction: listRules.foreign.length > FOREIGN_LIMIT,
        action: listRules.foreign.length - FOREIGN_LIMIT + ' yabancıyı listeden çıkar',
        ...badge(foreignLevel),
      },
      {
        level: youngLevel,
        title: 'TFF · +4 GENÇ KONTENJANI',
        value: listRules.youngForeign.length,
        // The gauge reads against the fixed "+4" ceiling, not against how many are
        // required right now — that number moves with every signing and made the bar
        // jump around. The required count still drives the badge and the action line.
        limit: '/ ' + YOUNG_FOREIGN_ALLOWANCE + ' üst sınır (2003+)',
        ratio:
          Math.min(100, (listRules.youngForeign.length / YOUNG_FOREIGN_ALLOWANCE) * 100) + '%',
        detail:
          '10 yabancı için yaş kriteri yok. 10\'u aşan her yabancının 01.01.2003 veya sonrası doğumlu olması zorunlu.',
        hasAction: listRules.overBase > listRules.youngForeign.length,
        action:
          listRules.overBase - listRules.youngForeign.length + ' adet 2003+ doğumlu yabancı gerekli',
        ...badge(youngLevel),
      },
      {
        level: listLevel,
        title: 'UEFA · LİSTE A',
        value: listRules.listA.length,
        limit: '/ ' + listRules.listACapacity + ' kullanılabilir slot',
        ratio: Math.min(100, (listRules.listA.length / LIST_A_LIMIT) * 100) + '%',
        detail:
          'Azami 25 oyuncu. En az 8 yetiştirilmiş oyuncu (' +
          listRules.homegrown.length +
          ' var), bunların en az 4\'ü kulüp altyapısı (' +
          listRules.clubTrained.length +
          ' var).',
        hasAction: listRules.homegrownShort > 0 || listRules.clubTrainedShort > 0,
        action:
          (listRules.homegrownShort > 0
            ? listRules.homegrownShort + ' yetiştirilmiş oyuncu eksik. '
            : '') +
          (listRules.clubTrainedShort > 0
            ? listRules.clubTrainedShort + ' altyapı oyuncusu eksik.'
            : ''),
        ...badge(listLevel),
      },
      {
        level: wageLevel,
        title: 'FİNANS · MAAŞ YÜKÜ',
        value: money(squadRules.totalWage),
        limit: '/ ' + money(wageCap) + ' bütçe',
        ratio: Math.min(100, (squadRules.totalWage / wageCap) * 100) + '%',
        detail:
          'Sahip olunan kadro: ' +
          owned.length +
          ' oyuncu · değer ' +
          money(squadRules.totalValue) +
          ' · yaş ort. ' +
          decimal(squadRules.averageAge, 1) +
          '. Planlı çıkışlar sonrası maaş ' +
          money(squadRules.totalWage - plannedWage) +
          '.',
        hasAction: squadRules.totalWage > wageCap,
        action: money(squadRules.totalWage - wageCap) + ' tasarruf gerekiyor',
        ...badge(wageLevel),
      },
    ];
  }, [owned, listRules, squadRules, wageCap]);

  const violationCount = cards.filter((card) => card.level === 'violation').length;
  const railLabel = violationCount ? violationCount + ' İHLAL' : 'TEMİZ';

  const budget = useMemo(() => {
    const over = squadRules.totalWage > wageCap;
    return {
      text: money(wageCap) + ' / yıl',
      ratio: Math.min(100, (squadRules.totalWage / wageCap) * 100) + '%',
      bar: over ? C.red : C.ink,
      note: over
        ? 'Bütçe ' + money(squadRules.totalWage - wageCap) + ' aşıldı.'
        : 'Kullanılabilir alan: ' + money(wageCap - squadRules.totalWage) + '.',
    };
  }, [squadRules, wageCap]);

  const toggle = () => dispatch({ type: 'togglePanel', panel: 'rules' });

  return (
    <section
      style={{
        gridColumn: '1',
        order: 1,
        borderRight: '2px solid ' + C.ink,
        borderBottom: '2px solid ' + C.ink,
        background: C.bg,
        minWidth: 0,
      }}
    >
      {!open && (
        <div
          onClick={toggle}
          title="Kural denetimini aç"
          style={{
            cursor: 'pointer',
            padding: '10px 0 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            minHeight: 440,
            background: C.surface,
            borderBottom: '2px solid ' + C.ink,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 800, color: C.red, lineHeight: 1 }}>»</span>
          <span
            style={{
              writingMode: 'vertical-rl',
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.16em',
            }}
          >
            KURAL DENETİMİ
          </span>
          <span
            style={{
              writingMode: 'vertical-rl',
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.1em',
              padding: '5px 2px',
              background: violationCount ? C.red : C.ink,
              color: C.bg,
            }}
          >
            {railLabel}
          </span>
        </div>
      )}

      {open && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '9px 10px 9px 16px',
              borderBottom: '2px solid ' + C.ink,
              background: C.surface,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em' }}>
              KURAL DENETİMİ
            </span>
            <button
              onClick={toggle}
              style={{
                border: '2px solid ' + C.ink,
                background: C.bg,
                color: C.ink,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.08em',
                padding: '4px 9px',
                cursor: 'pointer',
              }}
            >
              « GİZLE
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>
            {cards.map((card) => (
              <div
                key={card.title}
                style={{
                  padding: '13px 16px',
                  borderBottom: '1px solid ' + C.line,
                  borderRight: '1px solid ' + C.line,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.02em' }}>
                    {card.title}
                  </span>
                  <span
                    style={{
                      fontSize: 9.5,
                      fontWeight: 800,
                      letterSpacing: '0.1em',
                      padding: '3px 7px',
                      background: card.bg,
                      color: card.fg,
                      border: '1px solid ' + card.border,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {card.label}
                  </span>
                </div>

                <div
                  style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 9 }}
                >
                  <span
                    style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, color: card.valueFg }}
                  >
                    {card.value}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>
                    {card.limit}
                  </span>
                </div>

                <div
                  style={{ height: 6, background: '#dcd9d7', marginTop: 9, position: 'relative' }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: card.ratio,
                      background: card.barBg,
                    }}
                  />
                </div>

                <div
                  style={{
                    fontSize: 11,
                    lineHeight: 1.45,
                    color: C.ink2,
                    marginTop: 9,
                    textWrap: 'pretty',
                  }}
                >
                  {card.detail}
                </div>

                {card.hasAction && (
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: C.deep,
                      marginTop: 7,
                      letterSpacing: '0.02em',
                    }}
                  >
                    → {card.action}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ padding: '13px 16px', borderBottom: '2px solid ' + C.ink }}>
            <div
              style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}
            >
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em' }}>
                MAAŞ BÜTÇESİ
              </span>
              <span style={{ fontSize: 12, fontWeight: 800 }}>{budget.text}</span>
            </div>
            <input
              type="range"
              min={40}
              max={200}
              step={5}
              value={wageCap}
              onChange={(e) =>
                dispatch({ type: 'setWageCap', value: parseInt(e.target.value, 10) })
              }
              style={{ width: '100%', marginTop: 9 }}
            />
            <div
              style={{
                height: 10,
                background: '#dcd9d7',
                position: 'relative',
                border: '1px solid ' + C.line,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: budget.ratio,
                  background: budget.bar,
                }}
              />
            </div>
            <div style={{ fontSize: 11, color: C.ink2, marginTop: 7 }}>{budget.note}</div>
          </div>
        </>
      )}
    </section>
  );
}
